import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import _ from 'lodash';
import { useDebounce } from '@/core/hooks/useDebounce.hook';
import { AgGridModules } from '@/core/tableUtilities/tableUtilities.types';
import {
  addScreenshotSetup,
  autoSizeColumns,
  conditionTableComparator,
  firstNonEmptyValueAggFunc,
  getDomLayout,
  getRowId,
  lastNonEmptyValueAggFunc,
  onColumnMoved,
  rangeAggFunc,
  resizeAgGridHelper,
  restrictRowDataIfNecessary,
  simpleTableComparator,
  standardDeviationAggFunc,
} from '@/tableBuilder/tableBuilderAgGrid.utilities';
import {
  ChartOptionsChangedEvent,
  ChartToolPanelsDef,
  ColDef,
  ColumnResizedEvent,
  GridApi,
  GridOptions,
  GridReadyEvent,
  InitialGroupOrderComparatorParams,
  IRowNode,
  RowDataUpdatedEvent,
  RowSelectionOptions,
} from 'ag-grid-community';
import { headlessRenderMode } from '@/services/headlessCapture.utilities';
import { DEBOUNCE } from '@/core/core.constants';
import { COLUMNS_AND_STATS, TREND_COLORS } from '@/trendData/trendData.constants';
import { GroupingHeader } from '@/tableBuilder/tableComponents/GroupingHeader.molecule';
import { doTrack } from '@/track/track.service';
import { setChartViewConditionSettings, setChartViewSettings } from '../tableBuilder.actions';
import {
  AUTO_GROUP_COL_DEF,
  CONDITION_TABLE_TRANSPOSED_HEADER_COLUMN,
  DEFAULT_COL_DEF,
  DEFAULT_TABLE_ROW_HEIGHT,
  HEADLESS_AG_RENDERING_CLASS,
  SIMPLE_TABLE_TRANSPOSED_HEADER_COLUMN,
  TABLE_BUILDER,
  TableBuilderHeaderType,
  TableBuilderMode,
} from '../tableBuilder.constants';
import { onColumnResized } from '../tableBuilderAgGrid.actions';
import {
  getChartToolPanelsDef,
  getSeeqAgChartTheme,
  gridOptions,
  persistChart,
  regenerateChart,
} from '../tableBuilderAgChart.utilities';
import {
  AgGridChartMenuSettings,
  AgGridChartPanelTabs,
  AgGridRowIds,
  ConditionTableColumnsAndRows,
  ConditionTableData,
  ConditionTableHeader,
  SimpleTableRow,
  TableBuilderConditionAgGridProps,
  TableBuilderHeaders,
  TableBuilderSimpleAgGridProps,
} from '../tableBuilder.types';
import { useResizeGroupingBar } from '../hooks/useResizeGroupingBar.hook';

interface IUseAgGridProps {
  agGridApi: GridApi<any> | undefined;
  columnDefs: ColDef[];
  chartWrapperRef: MutableRefObject<HTMLDivElement | null>;
  data: Record<string, any>[];
  props: TableBuilderConditionAgGridProps | TableBuilderSimpleAgGridProps;
  rowNodeOutsideGridRef: MutableRefObject<IRowNode<any> | undefined>;
  setAgGridApi: (api: GridApi) => void;
  showChartView: boolean;
  t: (key: string) => string;
  tableBuilderMode: TableBuilderMode;
  wrapperRef: MutableRefObject<HTMLDivElement | null>;
  conditionTableData?: ConditionTableData;
  shouldAutoSize?: boolean;
  simpleTableData?: SimpleTableRow[];
  rowIds?: AgGridRowIds;
  chartViewColors?: string[] | null;
  conditionColumns?: ConditionTableColumnsAndRows;
  headers?: TableBuilderHeaders;
  chartMenuSettings: AgGridChartMenuSettings;
}

interface IAgGridProps {
  aggFuncs: Record<string, any>;
  animateRows: boolean;
  autoGroupColumnDef?: ColDef<any>;
  chartThemes: string[];
  chartToolPanelsDef: ChartToolPanelsDef;
  customChartThemes: Record<string, any>;
  defaultColDef: ColDef;
  domLayout: 'print' | 'autoHeight' | 'normal';
  enableCharts: boolean;
  functionsReadOnly: boolean;
  getRowId: (data: any) => string;
  gridOptions: GridOptions;
  groupAllowUnbalanced: boolean;
  initialGroupOrderComparator: ((params: InitialGroupOrderComparatorParams) => number) | undefined;
  modules: AgGridModules[];
  onChartOptionsChanged: (params: ChartOptionsChangedEvent) => void;
  onColumnMoved: (event: any) => void;
  onColumnResized: ((event: ColumnResizedEvent<any, any>) => void) | undefined;
  onGridReady: (params: GridReadyEvent) => void;
  onRowDataUpdated: (e: RowDataUpdatedEvent) => void;
  onRowDragLeave: (event: any) => void;
  overlayNoRowsTemplate: string;
  popupParent: HTMLElement | null;
  rowData: any[];
  rowDragManaged: boolean;
  rowHeight: number;
  rowSelection: RowSelectionOptions<any, any>;
  suppressAggFuncInHeader: boolean;
  suppressColumnVirtualisation: boolean;
  suppressContextMenu: boolean;
  suppressDragLeaveHidesColumns: boolean;
  suppressFieldDotNotation: boolean;
}

interface IUseAgGridReturn {
  autoSizeHelper: () => void;
  sharedAgGridProps: IAgGridProps;
  showTable: boolean;
}

const CHART_THEMES = ['seeq-theme'];
const CONTENT_CHART_TOOLS_PANEL_DEF = { panels: [] };
const COMBO_CHARTS = ['columnLineCombo', 'areaColumnCombo', 'customCombo'];

const getChartViewSettings = (
  tableBuilderMode: TableBuilderMode,
  props: TableBuilderSimpleAgGridProps | TableBuilderConditionAgGridProps,
) => {
  switch (tableBuilderMode) {
    case TableBuilderMode.Simple:
      return (props as TableBuilderSimpleAgGridProps).chartViewSettings;
    case TableBuilderMode.Condition:
      return (props as TableBuilderConditionAgGridProps).chartViewConditionSettings;
    default:
      return null;
  }
};

const getAutoGroupByType = (tableBuilderMode: TableBuilderMode, columnDefs: ColDef[], t: (key: string) => string) => {
  const BASE_AUTO_GROUP_COL_DEF = {
    ...AUTO_GROUP_COL_DEF,
    headerName: t('TABLE_BUILDER.ENABLE_GROUPING_BAR'),
    headerComponent: GroupingHeader,
  };

  switch (tableBuilderMode) {
    case TableBuilderMode.Simple:
      return { ...BASE_AUTO_GROUP_COL_DEF };
    case TableBuilderMode.Condition:
      return {
        ...BASE_AUTO_GROUP_COL_DEF,
        ..._.pick(columnDefs[0], 'valueFormatter', 'valueGetter', 'field'),
      };
    default:
      return { ...BASE_AUTO_GROUP_COL_DEF };
  }
};

const useAgGridProps = ({
  agGridApi,
  chartWrapperRef,
  columnDefs,
  conditionTableData,
  data,
  props,
  setAgGridApi,
  shouldAutoSize,
  showChartView,
  simpleTableData,
  t,
  tableBuilderMode,
  rowNodeOutsideGridRef,
  wrapperRef,
  rowIds = {},
  chartViewColors = TREND_COLORS,
  conditionColumns,
  chartMenuSettings,
}: IUseAgGridProps): IUseAgGridReturn => {
  const {
    afterChartUpdate,
    areAllRowsExpanded,
    headers,
    simpleColumns,
    isTransposed,
    darkMode,
    handleRowGroupOpened,
    moveColumn,
    updateContentMeasurements,
    autoGroupColumn,
    onAgGridReady,
    contentResizeState,
    useSignalColorsInChart,
    fromDashboard,
  } = props;

  const handleUpdateChartViewSettings =
    tableBuilderMode === TableBuilderMode.Simple ? setChartViewSettings : setChartViewConditionSettings;
  const [temporarilyExcludedColumnIds, setTemporarilyExcludedColumnIds] = useState<string[] | null>(null);
  const [popupParent, setPopupParent] = useState<HTMLElement | null>(null);
  const [lastTransposed, setLastTransposed] = useState(!isTransposed);
  const [isInitializingChart, setIsInitializingChart] = useState(true);
  const isInteractiveContent = !_.isNil(updateContentMeasurements);
  const chartViewSettings = getChartViewSettings(tableBuilderMode, props);
  const showCapsuleHeaders = headers?.type !== TableBuilderHeaderType.None;
  const showTableHeaders = !!simpleColumns.find((column) => column.key === COLUMNS_AND_STATS.name.key);
  const showTable = isTransposed === lastTransposed;
  const autoSizeColumnsDebounce = useDebounce(autoSizeColumns, DEBOUNCE.MEDIUM_WITH_HEADLESS_RENDER_SUPPORT);
  const isContent = isInteractiveContent || headlessRenderMode();
  const transposedHeaderColumn =
    tableBuilderMode === TableBuilderMode.Simple
      ? SIMPLE_TABLE_TRANSPOSED_HEADER_COLUMN
      : CONDITION_TABLE_TRANSPOSED_HEADER_COLUMN;

  const onGridReady = (params: GridReadyEvent) => {
    setAgGridApi(params.api);
    addScreenshotSetup(wrapperRef.current, showChartView);
  };

  const seeqTheme = useMemo(
    () => ({
      'seeq-theme': getSeeqAgChartTheme({ agGridApi, rowIds, darkMode, chartViewColors }),
    }),
    [rowIds, darkMode, chartViewColors],
  );

  const onRowDataUpdated = useCallback(() => {
    // This helps to redraw the rows after data update
    // This is needed because the grid does not redraw consistently rows after data update
    agGridApi?.redrawRows();
  }, [agGridApi]);

  const autoSizeHelper = useCallback(
    () =>
      resizeAgGridHelper(
        autoSizeColumnsDebounce,
        columnDefs,
        autoGroupColumn,
        isInteractiveContent,
        agGridApi,
        wrapperRef.current,
        showTable,
        setLastTransposed,
        isTransposed,
        data.length,
        updateContentMeasurements,
        onAgGridReady,
        fromDashboard,
      ),
    [
      agGridApi,
      autoGroupColumn,
      autoSizeColumnsDebounce,
      columnDefs,
      isInteractiveContent,
      isTransposed,
      onAgGridReady,
      showTable,
      updateContentMeasurements,
      contentResizeState,
      handleRowGroupOpened,
      fromDashboard,
    ],
  );

  useResizeGroupingBar({ wrapperRef, autoGroupColumn });

  useEffect(() => {
    if (shouldAutoSize) {
      autoSizeHelper();
    }
  }, [shouldAutoSize, autoSizeHelper]);

  /* --------- Charts -----------  */
  useEffect(() => {
    if (!showChartView || !agGridApi || (!conditionTableData?.headers?.length && !simpleTableData?.length)) {
      return;
    }

    setPopupParent(chartWrapperRef?.current);
    agGridApi?.getChartRef(_.first(agGridApi?.getChartModels())?.chartId ?? '')?.destroyChart();
    if (!chartViewSettings?.chartType || !chartWrapperRef?.current) {
      regenerateChart('#tableChart', agGridApi, columnDefs);
    } else {
      const { cellRange, excludedColumnIds } = chartViewSettings;
      const colIds = columnDefs?.map((col) => col.colId) || [];

      // Recalculate cell range if no matching columns are found
      const newSettings = {
        ...chartViewSettings,
        cellRange: {
          ...cellRange,
          columns: colIds.filter((colId) => !excludedColumnIds?.includes(colId)),
        },
        chartPalette: {
          strokes:
            _.isArray(chartViewColors) && chartViewColors.length > 0
              ? chartViewColors
              : chartViewSettings?.chartPalette?.strokes,
          fills:
            _.isArray(chartViewColors) && chartViewColors.length > 0
              ? chartViewColors
              : chartViewSettings?.chartPalette?.fills,
        },
      };

      setIsInitializingChart(true);
      agGridApi.restoreChart(_.cloneDeep(newSettings), chartWrapperRef.current!);
      afterChartUpdate?.();
    }

    wrapperRef.current?.classList.remove(HEADLESS_AG_RENDERING_CLASS);
  }, [showChartView, data, agGridApi, showCapsuleHeaders, darkMode, useSignalColorsInChart]);

  useEffect(() => {
    if (autoGroupColumn && areAllRowsExpanded) {
      agGridApi?.setColumnsVisible([transposedHeaderColumn], false);
    } else {
      agGridApi?.setColumnsVisible([transposedHeaderColumn], true);
    }
  }, [autoGroupColumn, agGridApi, showTableHeaders, showCapsuleHeaders, areAllRowsExpanded]);

  useEffect(() => {
    if (chartMenuSettings?.isOpened && showChartView && !isContent) {
      const chartId = _.first(agGridApi?.getChartModels())?.chartId;
      if (chartId) {
        agGridApi?.openChartToolPanel({
          chartId: chartId as string,
          panel: chartMenuSettings.panel as AgGridChartPanelTabs,
        });
      }
    }
  }, [chartMenuSettings, showChartView, isContent]);

  useEffect(() => {
    // true - when expand is clicked
    // false - when collapse is clicked
    // undefined - when user manually change the expand/collapse row
    if (areAllRowsExpanded) {
      expandAll(true);
      agGridApi?.expandAll();
    } else if (areAllRowsExpanded === false) {
      expandAll(false);
      agGridApi?.collapseAll();
    }
  }, [areAllRowsExpanded]);

  const expandAll = (expand: boolean) => {
    agGridApi?.forEachNode((node) => {
      if (node?.allChildrenCount ?? 0 > 0) {
        handleRowGroupOpened(undefined, node, expand);
      }
    });
  };

  const handleChartOptionsChanged = useCallback(
    (params: ChartOptionsChangedEvent) => {
      if (!agGridApi || !showChartView || isInitializingChart) {
        // This method is always called once when the chart is initialized, so don't persist during initialization
        if (isInitializingChart) {
          setIsInitializingChart(false);
        }
        return;
      }

      const currentChartModel = _.cloneDeep(_.first(agGridApi?.getChartModels()));

      /* This forces a restore when going from combo chart to non-combo chart to fix an AgGrid error */
      if (
        currentChartModel?.chartId &&
        COMBO_CHARTS.includes(chartViewSettings?.chartType) &&
        !COMBO_CHARTS.includes(currentChartModel?.chartType)
      ) {
        agGridApi?.getChartRef(currentChartModel.chartId)?.destroyChart();
        agGridApi?.restoreChart(currentChartModel, chartWrapperRef.current!);
      }

      const allColumnIds = columnDefs?.map((col) => col.colId) || [];
      const excludedIds =
        allColumnIds.filter(
          (colId): colId is string =>
            typeof colId === 'string' && !currentChartModel?.cellRange?.columns?.includes(colId),
        ) ??
        chartViewSettings?.excludedColumnIds ??
        [];

      doTrack(TABLE_BUILDER, 'chart type changed', params?.chartType);
      persistChart(isInteractiveContent, agGridApi, handleUpdateChartViewSettings, excludedIds);
      setTemporarilyExcludedColumnIds(null);
    },
    [
      agGridApi,
      chartViewSettings?.chartType,
      chartWrapperRef,
      handleUpdateChartViewSettings,
      isInteractiveContent,
      showChartView,
      isInitializingChart,
      temporarilyExcludedColumnIds,
    ],
  );

  const initialGroupOrderComparator = useMemo(() => {
    if (showChartView) return;

    return (params: InitialGroupOrderComparatorParams) => {
      const isSimpleMode = tableBuilderMode === TableBuilderMode.Simple;
      if (params.nodeA.rowIndex === undefined || params.nodeB.rowIndex === undefined || isSimpleMode === isTransposed) {
        return 0;
      }

      const agRowGroupColumn = params.nodeA.rowGroupColumn;
      const cols = isSimpleMode ? simpleColumns : conditionColumns?.rows;
      const seeqRowGroupColumn = cols?.find((column) => column.key === agRowGroupColumn?.getColId());

      if (!seeqRowGroupColumn || seeqRowGroupColumn.sort === undefined) {
        return 0;
      }

      const isDescending = seeqRowGroupColumn.sort.direction === 'desc';
      const header = conditionTableData?.headers.find((header) => header.key === seeqRowGroupColumn.key);

      // All leaf nodes of this group will have the same value for this column, so just grab the first
      const childNodeA = params.nodeA.allLeafChildren?.[0];
      const childNodeB = params.nodeB.allLeafChildren?.[0];

      if ((!isSimpleMode && !header) || !childNodeA || !childNodeB) {
        return 0;
      }

      if (isSimpleMode) {
        const value = simpleTableComparator(childNodeA, childNodeB, seeqRowGroupColumn);
        return seeqRowGroupColumn.sort.direction === 'asc' ? value : -value;
      } else {
        // isDescending isn't actually needed in the codepath we take in conditionTableComparator, but it is
        // needed afterwards
        const value = conditionTableComparator(isDescending, childNodeA, childNodeB, header as ConditionTableHeader);
        return isDescending ? -value : value;
      }
    };
  }, [
    conditionColumns?.rows,
    conditionTableData?.headers,
    isTransposed,
    showChartView,
    simpleColumns,
    tableBuilderMode,
  ]);

  const chartToolsPanelDef =
    !isContent && chartMenuSettings.isOpened ? { defaultToolPanel: chartMenuSettings?.panel || 'settings' } : {};

  return {
    showTable,
    autoSizeHelper,
    sharedAgGridProps: {
      aggFuncs: {
        stdDev: standardDeviationAggFunc,
        range: rangeAggFunc,
        first: firstNonEmptyValueAggFunc,
        last: lastNonEmptyValueAggFunc,
      },
      animateRows: !headlessRenderMode(),
      autoGroupColumnDef: getAutoGroupByType(tableBuilderMode, columnDefs, t),
      chartThemes: CHART_THEMES,
      chartToolPanelsDef: isContent
        ? CONTENT_CHART_TOOLS_PANEL_DEF
        : {
            ...getChartToolPanelsDef(tableBuilderMode === TableBuilderMode.Simple),
            ...chartToolsPanelDef,
          },
      customChartThemes: seeqTheme,
      defaultColDef: DEFAULT_COL_DEF,
      domLayout: getDomLayout(isInteractiveContent, data.length, fromDashboard),
      enableCharts: true,
      functionsReadOnly: isInteractiveContent, // Re-enable as part of CRAB-40362
      getRowId,
      gridOptions,
      groupAllowUnbalanced: true, // Needed to ensure UOM row is not grouped
      initialGroupOrderComparator,
      modules: [
        AgGridModules.SetFilterModule,
        AgGridModules.ColumnMenuModule,
        AgGridModules.ContextMenuModule,
        AgGridModules.ColumnsToolPanelModule,
        AgGridModules.RowAutoHeightModule,
        AgGridModules.RowStyleModule,
        AgGridModules.PaginationModule,
        AgGridModules.RowDragModule,
        AgGridModules.RowNumbersModule,
        AgGridModules.PinnedRowModule,
        AgGridModules.RowSelectionModule,
        AgGridModules.RowApiModule,
        AgGridModules.TextFilterModule,
        AgGridModules.DateFilterModule,
        AgGridModules.MultiFilterModule,
        AgGridModules.LocaleModule,
        AgGridModules.ClipboardModule,
        AgGridModules.RowGroupingModule,
        AgGridModules.RowGroupingPanelModule,
        AgGridModules.ColumnAutoSizeModule,
        AgGridModules.CellSpanModule,
        AgGridModules.CellApiModule,
        AgGridModules.CellStyleModule,
        AgGridModules.ColumnApiModule,
        AgGridModules.ColumnMenuModule,
        AgGridModules.IntegratedChartsModule,
        AgGridModules.TextEditorModule,
        AgGridModules.ClientSideRowModelApiModule,
        AgGridModules.PivotModule,
        AgGridModules.ScrollApiModule,
        AgGridModules.RenderApiModule,
      ],
      onChartOptionsChanged: handleChartOptionsChanged,
      onColumnMoved: (event) => onColumnMoved(event, moveColumn),
      onColumnResized: isInteractiveContent ? undefined : onColumnResized,
      onGridReady,
      onRowDataUpdated,
      onRowDragLeave: (event) => {
        rowNodeOutsideGridRef.current = event.node;
      },
      overlayNoRowsTemplate: '<div></div>',
      popupParent,
      rowData: restrictRowDataIfNecessary(data),
      rowDragManaged: true,
      rowHeight: DEFAULT_TABLE_ROW_HEIGHT,
      rowSelection: {
        mode: 'multiRow',
        enableClickSelection: true,
        checkboxes: false,
        headerCheckbox: false,
      },
      suppressAggFuncInHeader: true,
      suppressColumnVirtualisation: true,
      suppressContextMenu: true,
      suppressDragLeaveHidesColumns: true,
      suppressFieldDotNotation: true,
      ...(!headlessRenderMode() ? {} : { getChartToolbarItems: () => [] }),
    },
  };
};

export default useAgGridProps;
